/**
 * IndexedDB wrapper for offline-first patient & queue caching.
 * Uses the native IDBDatabase API directly — no extra dependency.
 *
 * Stores:
 *   patients    — last 500 searched/viewed patients (key: id)
 *   opdQueue    — today's OPD queue tokens (key: token_number)
 *   draftQueue  — locally-created billing/vitals drafts pending sync (key: local_id)
 */

const DB_NAME    = 'aarogya-offline';
const DB_VERSION = 1;

let _db: IDBDatabase | null = null;

function openDb(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains('patients')) {
        const store = db.createObjectStore('patients', { keyPath: 'id' });
        store.createIndex('by_name',   'name',          { unique: false });
        store.createIndex('by_mobile', 'mobile',        { unique: false });
        store.createIndex('by_uhid',   'uhid',          { unique: false });
        store.createIndex('by_cached', 'cached_at',     { unique: false });
      }

      if (!db.objectStoreNames.contains('opdQueue')) {
        const q = db.createObjectStore('opdQueue', { keyPath: 'token_number' });
        q.createIndex('by_date', 'queue_date', { unique: false });
      }

      if (!db.objectStoreNames.contains('draftQueue')) {
        const d = db.createObjectStore('draftQueue', { keyPath: 'local_id', autoIncrement: true });
        d.createIndex('by_type',   'type',      { unique: false });
        d.createIndex('by_status', 'status',    { unique: false });
      }
    };

    req.onsuccess = (e) => {
      _db = (e.target as IDBOpenDBRequest).result;
      resolve(_db);
    };

    req.onerror = (e) => {
      reject((e.target as IDBOpenDBRequest).error);
    };
  });
}

function tx(storeName: string, mode: IDBTransactionMode = 'readonly') {
  return openDb().then(db => db.transaction(storeName, mode).objectStore(storeName));
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((res, rej) => {
    req.onsuccess = () => res(req.result);
    req.onerror   = () => rej(req.error);
  });
}

// ── Patient cache ─────────────────────────────────────────────────

export async function cachePatient(patient: any): Promise<void> {
  const store = await tx('patients', 'readwrite');
  await promisify(store.put({ ...patient, cached_at: Date.now() }));

  // Evict oldest entries beyond 500
  const countReq = await promisify(store.count());
  if (countReq > 500) {
    const cursor = await promisify(
      store.index('by_cached').openCursor(null, 'next')
    ) as IDBCursorWithValue | null;
    if (cursor) await promisify(cursor.delete());
  }
}

export async function getCachedPatient(id: string): Promise<any | null> {
  const store = await tx('patients');
  const result = await promisify(store.get(id));
  return result ?? null;
}

export async function searchCachedPatients(query: string): Promise<any[]> {
  const store   = await tx('patients');
  const all     = await promisify(store.getAll()) as any[];
  const q       = query.toLowerCase();
  return all
    .filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.mobile?.includes(q) ||
      p.uhid?.toLowerCase().includes(q)
    )
    .slice(0, 20);
}

export async function bulkCachePatients(patients: any[]): Promise<void> {
  const store = await tx('patients', 'readwrite');
  const now   = Date.now();
  await Promise.all(patients.map(p => promisify(store.put({ ...p, cached_at: now }))));
}

// ── OPD queue cache ───────────────────────────────────────────────

export async function cacheOpdQueue(date: string, tokens: any[]): Promise<void> {
  const store = await tx('opdQueue', 'readwrite');
  await Promise.all(tokens.map(t => promisify(store.put({ ...t, queue_date: date }))));
}

export async function getCachedOpdQueue(date: string): Promise<any[]> {
  const store = await tx('opdQueue');
  const all   = await promisify(store.index('by_date').getAll(date)) as any[];
  return all.sort((a, b) => a.token_number - b.token_number);
}

export async function clearOpdQueueForDate(date: string): Promise<void> {
  const store  = await tx('opdQueue', 'readwrite');
  const range  = IDBKeyRange.only(date);
  const cursor = await promisify(store.index('by_date').openCursor(range)) as IDBCursorWithValue | null;
  if (!cursor) return;

  const deleteNext = (c: IDBCursorWithValue | null): Promise<void> => {
    if (!c) return Promise.resolve();
    return promisify(c.delete()).then(() =>
      promisify(c.continue() as unknown as IDBRequest<IDBCursorWithValue | null>)
        .then(deleteNext)
    );
  };

  await deleteNext(cursor);
}

// ── Draft queue (billing / vitals queued offline) ─────────────────

export interface OfflineDraft {
  local_id?:    number;       // auto-generated
  type:         'billing' | 'vitals' | 'opd_checkin';
  status:       'pending' | 'syncing' | 'synced' | 'failed';
  payload:      object;
  created_at:   number;       // epoch ms
  retry_count:  number;
  last_error?:  string;
}

export async function enqueueDraft(draft: Omit<OfflineDraft, 'local_id'>): Promise<number> {
  const store = await tx('draftQueue', 'readwrite');
  return promisify(store.add(draft)) as Promise<number>;
}

export async function getPendingDrafts(): Promise<OfflineDraft[]> {
  const store = await tx('draftQueue');
  const all   = await promisify(store.index('by_status').getAll('pending')) as OfflineDraft[];
  return all;
}

export async function updateDraftStatus(
  localId: number,
  status:  OfflineDraft['status'],
  error?:  string
): Promise<void> {
  const store = await tx('draftQueue', 'readwrite');
  const draft = await promisify(store.get(localId)) as OfflineDraft;
  if (!draft) return;
  await promisify(store.put({
    ...draft,
    status,
    ...(error && { last_error: error }),
    retry_count: draft.retry_count + (status === 'failed' ? 1 : 0),
  }));
}

export async function deleteDraft(localId: number): Promise<void> {
  const store = await tx('draftQueue', 'readwrite');
  await promisify(store.delete(localId));
}

export async function countPendingDrafts(): Promise<number> {
  const store = await tx('draftQueue');
  return promisify(store.index('by_status').count('pending'));
}
